import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { PromptQualityAnalysis, QualityFeedbackItem } from '../../services/api';

interface QualityReviewModalProps {
  open: boolean;
  analysis: PromptQualityAnalysis | null;
  originalText: string;
  isSaving: boolean;
  onAcceptChanges: () => void;
  onEditResubmit: () => void;
}

const SEVERITY_CLASSES: Record<QualityFeedbackItem['severity'], string> = {
  High: 'bg-red-100 text-red-900 border-b-2 border-red-500',
  Medium: 'bg-amber-100 text-amber-900 border-b-2 border-amber-500',
  Low: 'bg-blue-100 text-blue-900 border-b-2 border-blue-500',
};

const SUB_SCORE_LABELS: Array<{ key: keyof PromptQualityAnalysis['subScores']; label: string }> = [
  { key: 'clarity', label: 'Clarity' },
  { key: 'specificity', label: 'Specificity' },
  { key: 'outputFormat', label: 'Output Format' },
  { key: 'constraints', label: 'Constraints' },
  { key: 'tokenEfficiency', label: 'Token Efficiency' },
];

// Renders the original prompt text with problem phrases highlighted inline using the
// startOffset/endOffset pairs from the analysis feedback. Overlapping or out-of-range
// offsets are skipped rather than corrupting the rendered text.
function HighlightedText({ text, feedback }: Readonly<{ text: string; feedback: QualityFeedbackItem[] }>) {
  const sorted = [...feedback].sort((a, b) => a.startOffset - b.startOffset);
  const segments: React.ReactNode[] = [];
  let cursor = 0;

  sorted.forEach((item, idx) => {
    if (item.startOffset < cursor || item.endOffset > text.length || item.startOffset >= item.endOffset) {
      return;
    }
    if (item.startOffset > cursor) {
      segments.push(<span key={`plain-${idx}`}>{text.slice(cursor, item.startOffset)}</span>);
    }
    segments.push(
      <mark
        key={`mark-${idx}`}
        className={`rounded-sm px-0.5 cursor-help ${SEVERITY_CLASSES[item.severity] ?? SEVERITY_CLASSES.Medium}`}
        title={`${item.severity} — ${item.category}: ${item.reason}`}
      >
        {text.slice(item.startOffset, item.endOffset)}
      </mark>
    );
    cursor = item.endOffset;
  });
  if (cursor < text.length) {
    segments.push(<span key="plain-tail">{text.slice(cursor)}</span>);
  }
  return <>{segments}</>;
}

// FR-7.2 comparison modal: shown when the quality analysis scores below the
// auto-approve threshold. Left panel = original with highlighted feedback and
// sub-scores; right panel = suggested rewrite with projected score range.
export const QualityReviewModal: React.FC<QualityReviewModalProps> = ({
  open,
  analysis,
  originalText,
  isSaving,
  onAcceptChanges,
  onEditResubmit,
}) => {
  if (!analysis) return null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isSaving) onEditResubmit(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quality Review Required</DialogTitle>
          <DialogDescription>
            This prompt scored below the quality threshold. Review the suggested improvements before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          {/* Left panel — original */}
          <div className="rounded-md border border-gray-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Original</h3>
              <span className="text-sm font-bold text-red-600" aria-label={`Quality score ${analysis.qualityScore} of 10`}>
                {analysis.qualityScore}/10
              </span>
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
              <HighlightedText text={originalText} feedback={analysis.feedback} />
            </div>
            <div className="border-t pt-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sub-scores</h4>
              <ul className="space-y-1">
                {SUB_SCORE_LABELS.map(({ key, label }) => (
                  <li key={key} className="flex items-center justify-between text-xs text-gray-600">
                    <span>{label}</span>
                    <span className="flex items-center gap-2">
                      <span className="inline-block w-24 h-1.5 rounded bg-gray-200 overflow-hidden" aria-hidden="true">
                        <span
                          className="block h-full rounded bg-blue-500"
                          style={{ width: `${(analysis.subScores[key] ?? 0) * 10}%` }}
                        />
                      </span>
                      <span className="font-medium w-8 text-right">{analysis.subScores[key]}/10</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right panel — suggested rewrite */}
          <div className="rounded-md border border-green-200 bg-green-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">Suggested</h3>
              {/* "Projected" must stay visible — never present the range as a single confident number */}
              <span className="text-sm font-bold text-green-700">
                Projected: {analysis.expectedScoreRange}/10
              </span>
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto">
              {analysis.suggestedRewrite ?? 'No suggested rewrite available — use Edit & Resubmit to revise manually.'}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onEditResubmit} disabled={isSaving}>
            Edit &amp; Resubmit
          </Button>
          <Button onClick={onAcceptChanges} disabled={isSaving || !analysis.suggestedRewrite}>
            {isSaving ? 'Saving...' : 'Accept Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
